import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { HomeworkInfo } from "./homeworkSlice";
import { StudentInfo } from "../student/studentSlice";
import { HomeworkCheckInfo } from "./homeworkCheckSlice";
import { HomeworkCommentInfo } from "./homeworkComment";
import { homeworkCommentChange } from "./homeworkCommentChangeSlice";
import { homeworkCheckChange } from "./homeworkCheckChangeSlice";

export interface HomeworkReportState {
    state: LoadStateInfo;
    request?: RetrieveHomeworkReportRequest;
    response?: {
        students?: StudentInfo[];
        checks?: HomeworkCheckInfo[];
        comments?: HomeworkCommentInfo[];
    }
}

const initialState: HomeworkReportState = {
    state: notStarted(),
};


export interface RetrieveHomeworkReportRequest {
    homeworkId: number;
    classroomId: number;
}
export const retrieveHomeworkReport = createAsyncThunk(
    "retrieveHomeworkReport",
    async (param: RetrieveHomeworkReportRequest, thunkAPI) => {
        console.debug(`retrieve homework report for classroom ${param.classroomId} and homework ${param.homeworkId}`);

        const response = await fetch(`${getBackendUrl()}/api/homework/${param.homeworkId}/report/${param.classroomId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveHomeworkReportRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.homeworkReport;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveHomeworkReport skipped: ${status.state.state}`);
            return false;
        }
    }
);


export const homeworkReportSlice = createSlice({
    name: "homeworkReport",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        check: (state, action: PayloadAction<RetrieveHomeworkReportRequest>) => {
            if (state.state.state != LoadState.NotStarted
                && (state.request && (state.request.classroomId != action.payload.classroomId || state.request.homeworkId != action.payload.homeworkId))
            ) {
                console.debug(`homework report not retrieved for classroom ${action.payload.classroomId}`);
                state.state = notStarted();
                state.request = action.payload;
                state.response = undefined;
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.response = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveHomeworkReport.pending, (state, action) => {
                state.state = loading();
                state.response = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveHomeworkReport.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.response = action.payload;
                // state.list?.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
            })
            .addCase(retrieveHomeworkReport.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(homeworkCommentChange.fulfilled, (state, action) => {
                if(`${state.request?.classroomId}` == `${action.meta.arg.classroomId}` &&
                    `${state.request?.homeworkId}` == `${action.meta.arg.homeworkId}`) {
                    console.debug(`${action.type} :: updating comment in report`);
                } else {
                    console.debug(`${action.type} :: comment change does not match report request, skipping update`);
                    return;
                }
                const studentId = action.meta.arg.studentId;
                if (state.response) {
                    if (!state.response.comments) {
                        state.response.comments = [];
                    }
                    const comment = action.payload as HomeworkCommentInfo;
                    const comments = state.response.comments;
                    const existingIndex = comments?.findIndex(c => 
                        `${c.studentId}` == `${studentId}`
                    ) || -1;
                    if (existingIndex >= 0 && comments[existingIndex]) {
                        comments[existingIndex] = comment;
                    } else {
                        comments.push(comment);
                    }
                }
            })
            .addCase(homeworkCheckChange.fulfilled, (state, action) => {
                if(`${state.request?.classroomId}` == `${action.meta.arg.classroomId}` &&
                    `${state.request?.homeworkId}` == `${action.meta.arg.homeworkId}`) {
                    console.debug(`${action.type} :: updating check in report`);
                } else {
                    console.debug(`${action.type} :: check change does not match report request, skipping update`);
                    return;
                }
                const studentId = action.meta.arg.studentId;
                if (state.response) {
                    if (!state.response.checks) {
                        state.response.checks = [];
                    }
                    const check = action.payload as HomeworkCheckInfo;
                    const checks = state.response.checks;
                    const existingIndex = checks?.findIndex(c => 
                        `${c.studentId}` == `${studentId}`
                    ) || -1;
                    if (existingIndex >= 0 && checks[existingIndex]) {
                        checks[existingIndex] = check;
                    } else {
                        checks.push(check);
                    }
                }
            })
            ;
    },
});

// export const { } = routineRecordListSlice.actions;

export const selectHomeworkReport = (state: RootState) => state.homeworkReport;

export default homeworkReportSlice.reducer;
